import { compose, lifecycle, withHandlers } from 'recompose';
import { pickBy, isEqual } from 'lodash';
import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';

import { buildSearchLink, parseSearchLink } from 'utils/search';
import { INTERNAL_PAGE_TYPES } from 'config/constants';

/**
 * Determines if the query represents a storage landing page (not vehicle)
 * that requires site_content_service data (content, contentTitle, etc.)
 *
 * We only use getSearchLandingPage for storage searches on landing pages.
 * Vehicle searches (type=vehicle) should use getSearchResults because the
 * /api/search/landing endpoint doesn't handle vehicle filtering properly.
 */
function isStorageLandingPage(query) {
	const { pageType, type, searchType } = query || {};
	const isVehicleSearch = type === 'vehicle' || searchType === 'vehicle';

	// Don't use landing page endpoint for vehicle searches
	if (isVehicleSearch) {
		return false;
	}

	return (
		pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE ||
		pageType === INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE
	);
}

export default compose(
	withHandlers({
		onSearch:
			({ actions, history, searchParams }) =>
			(filterValues, delayFiltering = false) => {
				// Merge in any new filters and filter out any false filters
				// page: 1 must come AFTER filterValues to ensure pagination resets
				// when filters change (SF-19283)
				const params = pickBy({
					...searchParams,
					...filterValues,
					page: 1,
				});
				const newSearch = buildSearchLink(params);
				actions.updateSearchFilters(params);
				if (!delayFiltering) {
					history.push({ search: newSearch });
				}
			},
	}),
	lifecycle({
		componentDidUpdate(prevProps) {
			const { actions, loading, location, page } = this.props;

			// Do another search if the query params changed
			if (
				location.search &&
				!isEqual(location.search, prevProps.location.search)
			) {
				const query = parseSearchLink(location.search);
				// Use getSearchLandingPage for storage landing pages to ensure
				// content (including dynamic headlines) is refreshed (SF-19130).
				// Vehicle searches must use getSearchResults as the landing
				// endpoint doesn't handle vehicle filtering properly.
				if (isStorageLandingPage(query)) {
					actions.getSearchLandingPage(query, location.key);
				} else {
					actions.getSearchResults(query, location.key);
				}
			}

			// Only scroll to the top if the page changes
			if (prevProps.loading && !loading && page !== prevProps.page) {
				scrollToElement('#search-page-results', { offset: -16 });
			}
		},
	}),
);
